import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
  styled,
  tableCellClasses,
} from "@mui/material";
import {
  AiOutlineEye,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlinePlus,
} from "react-icons/ai";
import {
  deleteData,
  editData,
  fetchDataFromApi,
} from "../../../../../utils/api";
import { useSnackbar } from "../../../../../context/SnackbarProvider";
// Custom StyledTableCell and StyledTableRow
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--primaryColor)",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TermsAndConditionsList = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  // eslint-disable-next-line
  const [selectedId, setSelectedId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(() => { });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));

  // Fetch Terms and Conditions on component mount
  useEffect(() => {

    if (!token || !user || user.role !== "admin") {
      showSnackbar("Forbidden: Insufficient Permissions.", "error", "#f1b9b9");
      navigate('/')
    }

    const fetchTermsAndConditions = async () => {
      setLoading(true);
      fetchDataFromApi(`/api/terms-and-conditions`).then((res) => {
        setTermsAndConditions(res);
      })
      setLoading(false)
    };
    fetchTermsAndConditions();
  }, []);

  // Handle status change for Terms and Conditions
  const handleStatusChange = async (id, terms, newStatus) => {

    terms.isActive = newStatus;

    editData(`/api/terms-and-conditions/${id}`, terms).then((res) => {
      fetchDataFromApi(`/api/terms-and-conditions`).then((res) => {
        setTermsAndConditions(res);
        showSnackbar("Satus Updated Successfully!!", "success", "#aadbaa");
      })
    });

  };

  // Handle deletion of a Terms and Conditions record
  const handleDelete = async (id) => {

    await deleteData(`/api/terms-and-conditions/${id}`).then((res) => {
      setTermsAndConditions(termsAndConditions.filter((termsAndCondition) => termsAndCondition._id !== id));
      showSnackbar("T&C Deleted Successfully!!", "success", "#aadbaa");
    }).catch((error) => {
      const msg = error.message || "Internal srever error!!";
      showSnackbar(msg, "error", "#f1b9b9");
    })

    setOpenDeleteDialog(false);
  };

  // Open delete confirmation dialog
  const handleDeleteDialogOpen = (id) => {
    setSelectedId(id);
    setConfirmAction(() => () => handleDelete(id));
    setOpenDeleteDialog(true);
  };

  // Navigate to edit page
  const handleEdit = (terms) => {
    if (terms._id) {
      navigate(`/admin/legal/edit-terms-and-conditions/${terms._id}`);
    }
  };

  // Format date to DD-MM-YYYY
  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: "1rem",
            fontWeight: "600",
            color: "var(--textHeading)",
            marginBottom: "2rem",
          }}
        >
          Dashboard / Terms And Conditions List
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AiOutlinePlus />}
          onClick={() => navigate("/admin/legal/add-terms-and-conditions")}
          sx={{ width: "230px", padding: "12px 16px" }}
        >
          Add New T&C
        </Button>
      </Box>

      {loading ? (
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Card
          sx={{
            overflowX: "auto",
            borderRadius: 3,
            boxShadow: 2,
            border: "1px solid var(--borderCool)",
          }}
        >
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              boxShadow: 0,
              borderRadius: "0",
              maxHeight: "75vh",
            }}
          >
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    <strong>Title</strong>
                  </StyledTableCell>
                  <StyledTableCell>
                    <strong>Version</strong>
                  </StyledTableCell>
                  <StyledTableCell>
                    <strong>Updated At</strong>
                  </StyledTableCell>
                  <StyledTableCell>
                    <strong>Status</strong>
                  </StyledTableCell>
                  <StyledTableCell>
                    <strong>Action</strong>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {termsAndConditions.length > 0 ? (
                  termsAndConditions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((terms) => (
                      <StyledTableRow key={terms._id}>
                        <StyledTableCell>{terms.title}</StyledTableCell>
                        <StyledTableCell>{terms.version}</StyledTableCell>
                        <StyledTableCell>{formatDate(terms.updatedAt)}</StyledTableCell>
                        <StyledTableCell>
                          <Switch
                            checked={terms.isActive}
                            onChange={(e) => handleStatusChange(terms._id, terms, e.target.checked)}
                            color="primary"
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </StyledTableCell>
                        <StyledTableCell
                          style={{
                            minWidth: "100px",
                            display: "flex",
                            gap: "1rem",
                          }}
                        >
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/admin/legal/view-terms-and-conditions/${terms._id}`)}
                            sx={{
                              border: "1px solid var(--borderCool)",
                            }}
                          >
                            <AiOutlineEye style={{ fontSize: "1.15rem" }} />
                          </IconButton>

                          <IconButton
                            color="secondary"
                            onClick={() => handleEdit(terms)}
                            sx={{
                              border: "1px solid var(--borderCool)",
                            }}
                          >
                            <AiOutlineEdit style={{ fontSize: "1.15rem" }} />
                          </IconButton>

                          <IconButton
                            color="error"
                            onClick={() => handleDeleteDialogOpen(terms._id)}
                            sx={{
                              border: "1px solid var(--borderCool)",
                            }}
                          >
                            <AiOutlineDelete style={{ fontSize: "1.15rem" }} />
                          </IconButton>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography>
                        <strong>No any terms and condition available.</strong>
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={termsAndConditions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Terms And Conditions</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmAction} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TermsAndConditionsList;